import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";
import { formatDate } from "@/lib/format";

const L = {
  it: {
    eyebrow: "Newsletter",
    title: ["Archivio delle", "newsletter."],
    sub: "Tutte le email inviate ai nostri iscritti: articoli, guide e novità sul commercio internazionale.",
    empty: "Nessuna newsletter inviata per ora. Iscriviti dal footer per riceverle per primo.",
    back: "Torna alla home"
  },
  en: {
    eyebrow: "Newsletter",
    title: ["Newsletter", "archive."],
    sub: "Every email we sent our subscribers: articles, guides and news on international trade.",
    empty: "No newsletter sent yet. Subscribe from the footer to receive them first.",
    back: "Back to home"
  }
};

export default function NewsletterArchive() {
  const { lang } = useLang();
  const a = L[lang];
  const [items, setItems] = useState(undefined);

  useEffect(() => {
    document.title = lang === "it" ? "Archivio newsletter — LB Trade Advisory" : "Newsletter archive — LB Trade Advisory";
  }, [lang]);

  useEffect(() => {
    api
      .get("/newsletter/archive")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <>
      <section className="relative overflow-hidden bg-navy px-6 pb-16 pt-36 noise lg:px-10 lg:pb-20 lg:pt-44">
        <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="relative mx-auto max-w-4xl">
          <Reveal>
            <Link
              to="/"
              data-testid="archive-back-link"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/50 transition-colors hover:text-gold-bright"
            >
              <ChevronLeft className="h-4 w-4" /> {a.back}
            </Link>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold">
              <span className="h-px w-10 bg-gold/70" /> {a.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <h1 className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight text-ivory sm:text-5xl" data-testid="archive-title">
              {a.title?.[0]} <em className="text-gold-bright">{a.title?.[1]}</em>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ivory/65">{a.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="min-h-[40vh] bg-navy-light px-6 py-14 lg:px-10 lg:py-20">
        <div className="mx-auto max-w-4xl">
          {items === undefined ? (
            <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-ivory/40">…</p>
          ) : items.length === 0 ? (
            <p className="border border-gold/15 py-16 text-center text-sm text-ivory/50" data-testid="archive-empty">
              {a.empty}
            </p>
          ) : (
            <div className="space-y-6" data-testid="archive-list">
              {items.map((n, i) => {
                const body = (lang === "en" ? n.body_en : n.body_it) || n.body_it || n.body_en || "";
                const paragraphs = body.split("\n\n").filter(Boolean);
                return (
                  <Reveal key={i} delay={Math.min(i * 0.07, 0.3)}>
                    <article className="border border-white/10 bg-white/[0.02] p-7 lg:p-9" data-testid={`archive-item-${i}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="border border-gold/30 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-bright">
                          Newsletter
                        </span>
                        <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ivory/40">
                          {formatDate(n.sent_at, lang)}
                        </span>
                      </div>
                      <h2 className="mt-4 font-serif text-2xl leading-snug text-ivory">{n.subject}</h2>
                      <div className="mt-5 space-y-4">
                        {paragraphs.map((p, j) => (
                          <p key={j} className="text-sm leading-relaxed text-ivory/65 lg:text-base">
                            {p}
                          </p>
                        ))}
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
